<template>
    <div v-if="modelValue" class="pu-file">
        <img v-if="preview" :src="preview" alt="Vista previa del comprobante" />
        <span class="pu-name">{{ modelValue.name }}</span>
        <Button icon="pi pi-times" class="p-button-text p-button-rounded p-button-sm" aria-label="Quitar comprobante"
                @click="clear" />
    </div>
    <label v-else class="pu-drop" :class="{ 'pu-invalid': invalid, 'pu-busy': compressing }">
        <input type="file" accept="image/*" :disabled="compressing" @change="onFile" />
        <i class="pi pi-camera"></i>
        <span>{{ compressing ? 'Procesando la imagen…' : label }}</span>
    </label>
</template>

<script>
// Adjuntar la foto de un comprobante o voucher (un solo archivo), compartido por el cobro de ventas
// (AccountEntry) y el de mesas (PaymentEntryForm). Comprime en el navegador antes de subir: una foto
// de celular (2–5 MB) excede el límite del backend/nginx y el cobro fallaba con un 413 silencioso
// (ver service/imageCompression.js). Controlado: el archivo vive en el padre (`v-model`).
import { compressImage, IMAGE_MAX_BYTES } from '../../service/imageCompression';

export default {
    name: 'ProofUpload',
    props: {
        modelValue: { type: [File, Object], default: null },
        label: { type: String, default: 'Tomar foto o elegir imagen' },
        invalid: { type: Boolean, default: false },
    },
    emits: ['update:modelValue'],
    data() {
        return { compressing: false, preview: null };
    },
    watch: {
        modelValue: {
            immediate: true,
            handler(file) {
                if (this.preview) URL.revokeObjectURL(this.preview);
                this.preview = file && typeof URL !== 'undefined' && URL.createObjectURL ? URL.createObjectURL(file) : null;
            },
        },
    },
    beforeUnmount() {
        if (this.preview) URL.revokeObjectURL(this.preview);
    },
    methods: {
        async onFile(event) {
            const original = event.target.files && event.target.files[0];
            event.target.value = '';
            if (!original) return;
            this.compressing = true;
            let file = original;
            try { file = await compressImage(original); } catch (e) { file = original; }
            this.compressing = false;
            // Tope duro: si ni comprimida baja del límite (p.ej. HEIC que el navegador no decodifica),
            // se rechaza con un mensaje claro en vez de dejar que el servidor responda 413.
            if (file.size > IMAGE_MAX_BYTES) {
                this.$toast.add({ severity: 'error', summary: 'Comprobante muy pesado',
                                  detail: 'No se pudo reducir la imagen por debajo de 1 MB. Intenta con una captura de pantalla o una foto de menor resolución.',
                                  life: 6000 });
                return;
            }
            this.$emit('update:modelValue', file);
        },
        clear() {
            this.$emit('update:modelValue', null);
        },
    },
};
</script>

<style scoped>
.pu-drop { position: relative; display: flex; align-items: center; justify-content: center; gap: .5rem;
    border: 1px dashed var(--surface-400); border-radius: 8px; padding: .65rem; cursor: pointer; font-size: .85rem;
    color: var(--text-color-secondary); }
.pu-drop input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
.pu-drop:focus-within { outline: 2px solid var(--primary-color); outline-offset: 2px; }
.pu-invalid { border-color: #ef4444; }
.pu-busy { opacity: .7; }
.pu-file { display: flex; align-items: center; gap: .5rem; border: 1px solid var(--surface-border); border-radius: 8px;
    padding: .35rem .5rem; }
.pu-file img { width: 40px; height: 40px; object-fit: cover; border-radius: 6px; }
.pu-name { flex: 1; min-width: 0; font-size: .8rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
</style>
